/** EMR / Rx pad configuration defaults */

export const RX_SECTION_IDS = [
  { id: 'complaint', label: 'Chief Complaint', defaultVisible: true },
  { id: 'hpi', label: 'History of Present Illness', defaultVisible: true },
  { id: 'history', label: 'Medical History', defaultVisible: true },
  { id: 'vitals', label: 'Vitals', defaultVisible: true },
  { id: 'examination', label: 'Physical Examination', defaultVisible: true },
  { id: 'assessment', label: 'Diagnosis / Assessment', defaultVisible: true },
  { id: 'rx', label: 'Medications', defaultVisible: true },
  { id: 'labs', label: 'Investigations', defaultVisible: true },
  { id: 'plan', label: 'Advice & Plan', defaultVisible: true },
  { id: 'referral', label: 'Referral', defaultVisible: true },
  { id: 'soap', label: 'SOAP Notes', defaultVisible: false },
];

export const VITAL_FIELDS = [
  { id: 'bp', label: 'Blood Pressure', unit: 'mmHg' },
  { id: 'pulse', label: 'Pulse', unit: '/min' },
  { id: 'temp', label: 'Temperature', unit: '°F' },
  { id: 'spo2', label: 'SpO₂', unit: '%' },
  { id: 'rr', label: 'Respiratory Rate', unit: '/min' },
  { id: 'weight', label: 'Weight', unit: 'kg' },
  { id: 'height', label: 'Height', unit: 'cm' },
  { id: 'bmi', label: 'BMI', unit: '' },
];

export const CLINICAL_CALCULATORS = [
  { id: 'bmi', label: 'BMI' },
  { id: 'egfr', label: 'eGFR (CKD-EPI)' },
  { id: 'chads', label: 'CHADS₂ score' },
  { id: 'wells', label: 'Wells DVT' },
];

export const REGIONAL_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mr', label: 'Marathi' },
  { code: 'bn', label: 'Bengali' },
];

export const MED_INSTRUCTION_SAMPLES = {
  en: 'Take after food. Complete the full course.',
  hi: 'भोजन के बाद लें। पूरा कोर्स पूरा करें।',
  ta: 'உணவுக்குப் பிறகு எடுத்துக் கொள்ளுங்கள். முழு கோர்ஸையும் முடிக்கவும்.',
  te: 'భోజనం తర్వాత తీసుకోండి. పూర్తి కోర్సు పూర్తి చేయండి.',
};

export function defaultEmrSettings() {
  return {
    rxSections: RX_SECTION_IDS.map((s, i) => ({
      id: s.id, label: s.label, visible: s.defaultVisible, order: i,
    })),
    customSections: [],
    vitalsConfig: {
      fields: VITAL_FIELDS.map((f) => f.id),
      calculators: ['bmi', 'egfr'],
    },
    printSettings: {
      fontSize: 13,
      showQr: true,
      showHeader: true,
      bilingualAdvice: false,
      adviceLang: 'hi',
      medInstructionLang: 'hi',
    },
    fullTemplates: [],
    sectionTemplates: {
      symptoms: ['Fever 3 days', 'Cough with expectoration', 'Chest pain on exertion'],
      diagnosis: ['URTI', 'Hypertension', 'Type 2 DM'],
      advice: ['Plenty of fluids', 'Rest', 'Salt-restricted diet'],
      medications: [],
    },
    services: [
      { name: 'OPD Consultation', fee: 500, category: 'Consultation' },
      { name: 'Follow-up Consultation', fee: 300, category: 'Consultation' },
      { name: 'ECG', fee: 200, category: 'Procedure' },
      { name: 'Dressing', fee: 150, category: 'Procedure' },
    ],
    treatmentPackages: [
      { id: 'PKG-DEL', name: 'Normal Delivery Package', dept: 'Obstetrics', amount: 25000, includes: '3-day stay, delivery, routine tests' },
      { id: 'PKG-LSCS', name: 'LSCS Package', dept: 'Obstetrics', amount: 45000, includes: 'Surgery, 5-day stay, OT' },
      { id: 'PKG-HEALTH', name: 'Master Health Check-up', dept: 'General', amount: 3500, includes: 'CBC, LFT, Lipid, ECG, consult' },
    ],
    doctorAvailability: [],
    medSectionConfig: {
      showGeneric: true, showDuration: true, showInstructions: true, defaultDuration: '5 days',
    },
    smsSettings: {
      autoApptReminder: true, autoReceiptSms: false, signature: '— HCEN Hospital',
      postVisitReviewSms: false,
    },
    queueCustomSections: [],
    enableDentalChart: false,
    pushAnnouncements: [],
    enableOphthalmology: false,
    enableVaccinationChart: true,
    enableHealthAssessment: true,
    docTemplates: [
      { id: 'referral-letter', name: 'Referral Letter', body: 'Dear Colleague,\n\nI am referring {{patient}} ({{uhid}}) for your opinion regarding {{diagnosis}}.\n\nThank you.' },
      { id: 'sick-leave', name: 'Sick Leave Note', body: 'This is to certify that {{patient}} was unfit for duty from {{from}} to {{to}}.' },
    ],
    rxPrintTemplate: {
      showLogo: true, showDoctorReg: true, showSignature: true,
      footerText: 'Get well soon!', showQr: true, bilingualAdvice: false,
    },
    roleModuleAccess: {},
    workspaces: [],
  };
}

export function mergeEmrSettings(saved) {
  const base = defaultEmrSettings();
  if (!saved || typeof saved !== 'object') return base;
  return {
    ...base,
    ...saved,
    rxSections: saved.rxSections?.length ? saved.rxSections : base.rxSections,
    sectionTemplates: { ...base.sectionTemplates, ...(saved.sectionTemplates || {}) },
    vitalsConfig: { ...base.vitalsConfig, ...(saved.vitalsConfig || {}) },
    printSettings: { ...base.printSettings, ...(saved.printSettings || {}) },
    services: saved.services?.length ? saved.services : base.services,
    treatmentPackages: saved.treatmentPackages?.length ? saved.treatmentPackages : base.treatmentPackages,
    smsSettings: { ...base.smsSettings, ...(saved.smsSettings || {}) },
    medSectionConfig: { ...base.medSectionConfig, ...(saved.medSectionConfig || {}) },
    doctorAvailability: saved.doctorAvailability?.length ? saved.doctorAvailability : base.doctorAvailability,
    queueCustomSections: saved.queueCustomSections?.length ? saved.queueCustomSections : base.queueCustomSections,
    enableDentalChart: saved.enableDentalChart ?? base.enableDentalChart,
    pushAnnouncements: saved.pushAnnouncements?.length ? saved.pushAnnouncements : base.pushAnnouncements,
    enableOphthalmology: saved.enableOphthalmology ?? base.enableOphthalmology,
    enableVaccinationChart: saved.enableVaccinationChart ?? base.enableVaccinationChart,
    enableHealthAssessment: saved.enableHealthAssessment ?? base.enableHealthAssessment,
    docTemplates: saved.docTemplates?.length ? saved.docTemplates : base.docTemplates,
    rxPrintTemplate: { ...base.rxPrintTemplate, ...(saved.rxPrintTemplate || {}) },
    roleModuleAccess: saved.roleModuleAccess || base.roleModuleAccess,
    workspaces: saved.workspaces?.length ? saved.workspaces : base.workspaces,
  };
}

export function visibleEmrTabs(settings) {
  const merged = mergeEmrSettings(settings);
  const tabMap = {
    consult: 'complaint', history: 'history', vitals: 'vitals', assessment: 'assessment',
    rx: 'rx', labs: 'labs', plan: 'plan', notes: 'soap',
  };
  return merged.rxSections
    .filter((s) => s.visible && tabMap[s.id])
    .sort((a, b) => a.order - b.order)
    .map((s) => {
      const labels = {
        complaint: 'Consult', history: 'History', vitals: 'Vitals & Exam',
        assessment: 'Assessment', rx: 'Prescription', labs: 'Investigations',
        plan: 'Plan & Advice', soap: 'SOAP Notes',
      };
      return { id: tabMap[s.id], label: labels[s.id] || s.label };
    });
}

function parseSlotRange(range, stepMin) {
  const m = String(range || '').match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
  if (!m) return [];
  const start = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  const end = parseInt(m[3], 10) * 60 + parseInt(m[4], 10);
  const slots = [];
  for (let t = start; t < end; t += stepMin) {
    slots.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
  }
  return slots;
}

/** Morning + evening slot buckets for appointment booking UI. */
export function slotBucketsForDoctor(doctorAvailability, doctorName, {
  morningFallback = [], eveningFallback = [], slotMin = 20,
} = {}) {
  const row = (doctorAvailability || []).find((x) => x.name === doctorName);
  if (!row) return { morning: morningFallback, evening: eveningFallback };
  const step = Number(row.slotMin) || slotMin;
  return {
    morning: parseSlotRange(row.morning, step),
    evening: parseSlotRange(row.evening, step),
  };
}

/** Build appointment time slots from doctor availability config. */
export function slotsForDoctor(doctorAvailability, doctorName, opts = {}) {
  const { morning, evening } = slotBucketsForDoctor(doctorAvailability, doctorName, opts);
  return [...morning, ...evening];
}

/** Service names + fee presets for billing / reception counters. */
export function servicePresetsFromSettings(settings) {
  return (settings?.services || []).map((s) => ({
    label: s.name,
    amount: Number(s.fee || 0),
    category: s.category || 'General',
  }));
}

export function serviceNamesFromSettings(settings, extras = []) {
  const names = (settings?.services || []).map((s) => s.name).filter(Boolean);
  return [...new Set([...names, ...extras])];
}

/** Merge hospital print settings with a doctor's personal Rx template overrides. */
export function effectivePrintSettings(emrSettings, user = {}) {
  const merged = mergeEmrSettings(emrSettings);
  return {
    ...merged,
    rxPrintTemplate: { ...merged.rxPrintTemplate, ...(user.rxPrintTemplate || {}) },
  };
}

/** Normalize treatment packages from hospital settings for billing UI. */
export function packagesFromSettings(settings) {
  return (settings?.treatmentPackages || []).map((p, i) => ({
    id: p.id || `PKG-${i + 1}`,
    name: p.name,
    dept: p.dept || p.department || 'General',
    amount: Number(p.amount || 0),
    includes: p.includes || '',
    duration: p.duration || '—',
  }));
}
